import { useEffect } from "react";
import { useCart } from "@/contexts/CartContext";
import { useI18n } from "@/i18n/I18nProvider";
import { X, Plus, Minus, ShoppingBag, ArrowRight, Trash2 } from "lucide-react";

const currencySymbol = (code) => (code === "EUR" ? "€" : code === "USD" ? "$" : code === "GBP" ? "£" : code + " ");
const formatPrice = (amount, code = "EUR") => {
  const num = Number(amount).toFixed(2);
  const sym = currencySymbol(code);
  return code === "EUR" || code === "GBP" ? `${num} ${sym}` : `${sym}${num}`;
};

export default function CartDrawer() {
  const { t } = useI18n();
  const { items, drawerOpen, closeDrawer, updateQuantity, removeItem, subtotal, count, checkout } = useCart();

  // Lock body scroll while open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  // Esc close
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => e.key === "Escape" && closeDrawer();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, closeDrawer]);

  return (
    <div
      data-testid="cart-drawer"
      aria-hidden={!drawerOpen}
      className={`fixed inset-0 z-[90] transition-opacity duration-300 ${
        drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Backdrop */}
      <div
        onClick={closeDrawer}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-label={t.cart.title}
        className={`absolute top-0 right-0 h-full w-full sm:w-[440px] bg-[#0a0a0a] border-l border-[#1f1f29] flex flex-col shadow-2xl transition-transform duration-400 ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{
          backgroundImage: "linear-gradient(180deg, rgba(123,30,46,0.10) 0%, rgba(0,0,0,0) 40%)",
        }}
      >
        {/* Gold edge */}
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

        {/* Header */}
        <header className="px-6 py-5 border-b border-[#1f1f29] flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="h-px w-5 bg-[#A93043]" />
              <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80">
                {t.cart.eyebrow}
              </span>
            </div>
            <h2 className="font-display text-2xl uppercase text-white tracking-wider">
              {t.cart.title}
              <span className="ml-2 font-serif-luxury italic text-[#D4AF37] text-base">
                ({count})
              </span>
            </h2>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            data-testid="cart-close-btn"
            aria-label={t.cart.close}
            className="w-9 h-9 flex items-center justify-center border border-[#1f1f29] text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-5 py-12">
              <div className="w-14 h-14 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37]">
                <ShoppingBag className="w-6 h-6" />
              </div>
              <p className="font-display text-2xl uppercase text-white tracking-wide">
                {t.cart.emptyTitle}
              </p>
              <p className="font-body text-sm text-white/55 max-w-[260px] leading-relaxed">
                {t.cart.emptyBody}
              </p>
              <button
                type="button"
                onClick={closeDrawer}
                data-testid="cart-continue-shopping"
                className="mt-2 px-6 py-3 bg-[#D4AF37] text-black font-body text-xs tracking-[0.25em] uppercase hover:bg-[#A93043] hover:text-white transition-colors"
              >
                {t.cart.continue}
              </button>
            </div>
          ) : (
            <ul className="space-y-5">
              {items.map((item) => (
                <li
                  key={item.variantId}
                  data-testid={`cart-line-${item.variantId}`}
                  className="flex gap-4 border-b border-[#1f1f29] pb-5 last:border-b-0"
                >
                  {/* Image */}
                  <div className="w-20 h-24 shrink-0 bg-[#11111a] overflow-hidden">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20">
                        <ShoppingBag className="w-5 h-5" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 flex flex-col">
                    <h3 className="font-display text-sm uppercase tracking-wider text-white leading-snug line-clamp-2">
                      {item.title}
                    </h3>
                    {item.variantTitle && item.variantTitle !== "Default Title" && (
                      <p className="font-body text-[11px] text-white/50 mt-1 tracking-wider">
                        {item.variantTitle}
                      </p>
                    )}

                    <div className="mt-auto flex items-end justify-between pt-3">
                      {/* Qty */}
                      <div className="flex items-center border border-[#1f1f29]">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                          data-testid={`cart-qty-minus-${item.variantId}`}
                          aria-label="−"
                          className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-colors"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span
                          data-testid={`cart-qty-${item.variantId}`}
                          className="w-8 text-center font-body text-sm text-white"
                        >
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                          data-testid={`cart-qty-plus-${item.variantId}`}
                          aria-label="+"
                          className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Price */}
                      <div className="flex items-center gap-3">
                        <span className="font-display text-base text-[#D4AF37]">
                          {formatPrice(item.price * item.quantity, item.currency)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeItem(item.variantId)}
                          data-testid={`cart-remove-${item.variantId}`}
                          aria-label={t.cart.remove}
                          className="text-white/40 hover:text-[#A93043] transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <footer className="border-t border-[#1f1f29] px-6 py-5 space-y-4 bg-black/40">
            <div className="flex items-baseline justify-between">
              <span className="font-body text-xs tracking-[0.25em] uppercase text-white/55">
                {t.cart.subtotal}
              </span>
              <span
                data-testid="cart-subtotal"
                className="font-display text-2xl text-[#D4AF37]"
              >
                {formatPrice(subtotal, "EUR")}
              </span>
            </div>
            <p className="font-body text-[11px] text-white/40 leading-relaxed">
              {t.cart.shippingNote}
            </p>
            <button
              type="button"
              onClick={checkout}
              data-testid="cart-checkout-btn"
              className="w-full flex items-center justify-center gap-2 py-4 bg-[#D4AF37] text-black font-display text-sm tracking-[0.25em] uppercase hover:bg-[#A93043] hover:text-white transition-colors"
            >
              {t.cart.checkout}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={closeDrawer}
              data-testid="cart-continue-btn"
              className="w-full py-3 font-body text-[11px] tracking-[0.25em] uppercase text-white/55 hover:text-[#D4AF37] transition-colors"
            >
              {t.cart.continue}
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
}
